import { ReactNode } from 'react'
import { usePermissions, useAuthStore } from '../stores/authStore'
import { Shield, ArrowRight, Lock, KeyRound, UserCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from './ui/Button'
import toast from 'react-hot-toast'

interface PermissionGuardProps {
  permission?: string
  resource?: string
  action?: string
  children: ReactNode
}

export function PermissionGuard({ permission, resource, action, children }: PermissionGuardProps) {
  const { hasPermission } = usePermissions()
  const { switchActiveRole, activeRoleName } = useAuthStore()

  const requiredPerm = permission || (resource ? `${resource}.${action || 'read'}` : '')
  const allowed = hasPermission(requiredPerm, action)

  if (!allowed) {
    return (
      <div className="p-6 md:p-12 max-w-2xl mx-auto my-8 text-center card bg-white dark:bg-neutral-900 shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-6">
        {/* Lock Icon */}
        <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-red-500/10 dark:bg-red-500/20 animate-pulse" />
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white flex items-center justify-center shadow-lg shadow-red-500/30">
            <Lock className="w-8 h-8" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-xs font-semibold">
            <KeyRound className="w-3.5 h-3.5" />
            دسترسی به این بخش در نقش «{activeRoleName}» غیرفعال است
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
            شما به این بخش دسترسی ندارید
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 max-w-md mx-auto leading-relaxed">
            طبق چک‌باکس‌های علامت‌زده‌شده برای نقش <strong>«{activeRoleName}»</strong>، این قسمت از نرم‌افزار غیرفعال شده است. برای مشاهده، باید تیک مربوطه در تعریف نقش فعال گردد.
          </p>
        </div>

        {requiredPerm && (
          <div className="p-3 bg-neutral-50 dark:bg-neutral-800/60 rounded-xl inline-block border border-neutral-200 dark:border-neutral-700 text-xs font-mono text-neutral-600 dark:text-neutral-300">
            شناسه مجوز مورد نیاز: <span className="text-red-500 font-bold">{requiredPerm}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Button
            onClick={() => {
              switchActiveRole('مدیر سیستم')
              toast.success('نقش فعال به مدیر سیستم بازگردانده شد. اکنون تمام بخش‌ها باز می‌باشند.')
            }}
            className="w-full sm:w-auto gap-2 bg-primary-600 hover:bg-primary-700 text-white"
          >
            <Shield className="w-4 h-4" />
            بازگشت به نقش «مدیر سیستم» (دسترسی کامل)
          </Button>

          <Link to="/">
            <Button variant="outline" className="w-full sm:w-auto gap-2">
              <ArrowRight className="w-4 h-4" />
              داشبورد اصلی
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
